import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Trash2 } from 'lucide-react';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [alertPhone, setAlertPhone] = useState('');
  const [alertRestaurant, setAlertRestaurant] = useState('');
  const [alertAddress, setAlertAddress] = useState('');
  const [callingAlert, setCallingAlert] = useState(null);
  const [alertSuggestions, setAlertSuggestions] = useState([]);
  const [showAlertSuggestions, setShowAlertSuggestions] = useState(false);
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Calling state for review calls
  const [callingReview, setCallingReview] = useState(null);

  useEffect(() => {
    const searchQuery = searchParams.get('search') || '';
    setSearch(searchQuery);
    fetchReviews(searchQuery);
  }, [searchParams]);

  const fetchReviews = async (searchQuery = '') => {
    setLoading(true);
    try {
      // Use public reviews endpoint so it works for unauthenticated users too
      const response = await axios.get(`${API}/public/reviews`, {
        params: searchQuery ? { search: searchQuery } : {}
      });
      setReviews(response.data);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReviews(search);
  };

  // Handle restaurant search for discrete alert
  const handleAlertRestaurantChange = async (value) => {
    setAlertRestaurant(value);
    
    if (value.length >= 3) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/restaurants/search?q=${encodeURIComponent(value)}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        setAlertSuggestions(response.data);
        setShowAlertSuggestions(response.data.length > 0);
      } catch (error) {
        console.error('Failed to fetch restaurant suggestions:', error);
        setAlertSuggestions([]);
        setShowAlertSuggestions(false);
      }
    } else {
      setAlertSuggestions([]);
      setShowAlertSuggestions(false);
    }
  };

  // Handle selecting a restaurant for discrete alert
  const handleSelectAlertRestaurant = (restaurant) => {
    setAlertRestaurant(restaurant.restaurant_name);
    setAlertPhone(restaurant.phone || '');
    setAlertAddress(restaurant.address ? `${restaurant.address}${restaurant.city ? ', ' + restaurant.city : ''}${restaurant.state ? ', ' + restaurant.state : ''}` : '');
    setShowAlertSuggestions(false);
    setAlertSuggestions([]);
  };

  // Handle discrete alert from dashboard
  const handleDiscreteAlert = async (messageType) => {
    if (!alertPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    if (!user) {
      toast.error('Please login to trigger alerts');
      navigate('/auth');
      return;
    }
    
    setCallingAlert(messageType);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/calls/trigger-direct`, {
        phone_number: alertPhone,
        message_type: messageType,
        restaurant_name: alertRestaurant || 'Unknown Restaurant'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Alert initiated successfully!');
      setAlertPhone('');
      setAlertRestaurant('');
      setAlertAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger alert');
    } finally {
      setCallingAlert(null);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to delete your account');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { password: deletePassword, confirmation: deleteConfirmation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Your account has been permanently deleted');
      localStorage.removeItem('token');
      setShowDeleteDialog(false);
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleTriggerCall = async (reviewId, messageType) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to trigger calls');
      return;
    }

    setCallingReview(reviewId);
    try {
      const response = await axios.post(
        `${API}/calls/trigger`,
        { review_id: reviewId, message_type: messageType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Call initiated successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to trigger call';
      toast.error(errorMsg);
    } finally {
      setCallingReview(null);
    }
  };

  const getConditionBadge = (value) => {
    // Map 1-10 scale to 3 levels: 1-3=Nasty, 4-7=Okay, 8-10=Spotless
    if (value >= 1 && value <= 3) {
      return { label: 'Nasty', class: 'nasty-badge' };
    } else if (value >= 4 && value <= 7) {
      return { label: 'Okay', class: 'clean-badge' };
    } else if (value >= 8 && value <= 10) {
      return { label: 'Spotless', class: 'pristine-badge' };
    }
    return { label: 'Okay', class: 'clean-badge' }; // default
  };

  const getSuppliesBadge = (value) => {
    // Map 1-10 scale to 3 levels: 1-3=Bring Your Own, 4-7=Okay, 8-10=Great
    if (value >= 1 && value <= 3) {
      return { label: 'Bring Your Own!', class: 'byo-badge' };
    } else if (value >= 4 && value <= 7) {
      return { label: 'Okay', class: 'ok-badge' };
    } else if (value >= 8 && value <= 10) {
      return { label: 'Great', class: 'luxury-badge' };
    }
    return { label: 'Okay', class: 'ok-badge' }; // default
  };

  const getMaintenanceBadge = (value) => {
    // Map 1-10 scale to 3 levels: 1-3=Not At All, 4-7=Almost, 8-10=Yes
    if (value >= 1 && value <= 3) {
      return { label: 'Not At All', class: 'yuck-badge' };
    } else if (value >= 4 && value <= 7) {
      return { label: 'Almost', class: 'okay-badge' };
    } else if (value >= 8 && value <= 10) {
      return { label: 'Yes', class: 'swanky-badge' };
    }
    return { label: 'Almost', class: 'okay-badge' }; // default
  };

  return (
    <div className="min-h-screen toilet-humor-bg text-gray-900">
      {/* Header - Only Logo */}
      <header className="bg-yellow-50 border-b-4 border-black shadow-lg">
        <div className="w-full">
          {/* Full Width Header Image */}
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_loo-tracker/artifacts/2igfkpva_Background%20for%20APP%20Home%20Screen%20no%20head.jpg" 
              alt="The Loo Report™ - Know Before You Go!"
              className="w-full h-auto object-contain"
              style={{
                maxHeight: '147px',
                display: 'block'
              }}
            />
            <span className="absolute text-[12px] font-bold text-black" style={{ fontFamily: 'Arial, sans-serif', bottom: '45%', right: '28%' }}>™</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {/* Navigation Buttons */}
        <div className="flex items-center gap-2 flex-wrap justify-center mb-3">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-2 border-black text-black font-bold bg-yellow-400 hover:bg-white min-w-[140px]"
            data-testid="home-btn"
          >
            🏠 Home
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-2 border-black text-black font-bold bg-yellow-400 hover:bg-white min-w-[140px]"
            data-testid="submit-review-btn"
          >
            ✏️ Post a Review
          </Button>
          {user && user.is_admin && (
            <Button
              onClick={() => navigate('/admin')}
              className="bg-red-600 hover:bg-red-700 text-white font-bold border-2 border-black min-w-[140px]"
              data-testid="admin-panel-btn"
            >
              Admin Panel
            </Button>
          )}
          {user ? (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowDeleteDialog(true)}
                variant="outline"
                className="border-2 border-red-500 text-red-600 font-bold bg-white hover:bg-red-50 min-w-[50px]"
                title="Delete Account"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => {
                  localStorage.removeItem('token');
                  window.location.href = '/';
                }}
                variant="outline"
                className="border-2 border-black text-black font-bold bg-yellow-400 hover:bg-white min-w-[140px]"
              >
                Logout ({user.name})
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-black text-black font-bold bg-yellow-400 hover:bg-white min-w-[140px]"
            >
              Login / Register
            </Button>
          )}
        </div>

        {/* URGENT ALERT Box - Below Navigation */}
        <div className="bg-gradient-to-r from-red-100 via-yellow-100 to-red-100 p-4 rounded-lg border-4 border-black mb-4">
          <h3 className="text-red-600 text-lg font-bold text-center mb-3"
              style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '0.05em' }}>
            📞 URGENT ALERT! - Anonymously alert a restaurant
          </h3>
          <div className="flex flex-col gap-3 items-center justify-center mb-3 max-w-lg mx-auto">
            {/* Restaurant Name & Address - Combined field with autocomplete */}
            <div className="relative w-full">
              <Input
                type="text"
                value={alertAddress ? `${alertRestaurant} - ${alertAddress}` : alertRestaurant}
                onChange={(e) => {
                  // If editing, clear address and just set restaurant name
                  setAlertAddress('');
                  handleAlertRestaurantChange(e.target.value);
                }}
                onBlur={() => setTimeout(() => setShowAlertSuggestions(false), 200)}
                placeholder="Start typing restaurant name..."
                className="bg-white border-2 border-black text-black font-semibold w-full"
                autoComplete="off"
              />
              {showAlertSuggestions && alertSuggestions.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border-2 border-black rounded shadow-lg max-h-48 overflow-y-auto">
                  {alertSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectAlertRestaurant(suggestion)}
                      className="p-2 hover:bg-yellow-100 cursor-pointer border-b border-gray-200"
                      style={{ fontFamily: 'Fredoka, sans-serif' }}
                    >
                      <div className="font-bold text-gray-900 text-sm">{suggestion.restaurant_name}</div>
                      {(suggestion.address || suggestion.city) && (
                        <div className="text-xs text-gray-600">
                          {suggestion.address}{suggestion.city ? `, ${suggestion.city}` : ''}{suggestion.state ? `, ${suggestion.state}` : ''}
                        </div>
                      )}
                      {suggestion.phone && <div className="text-xs text-blue-600">{suggestion.phone}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Phone Number and Audio Alert Button - side by side */}
            <div className="flex gap-3 w-full">
              <Input
                type="tel"
                value={alertPhone}
                onChange={(e) => setAlertPhone(e.target.value)}
                placeholder="Restaurant phone number"
                className="bg-white border-2 border-black text-black font-semibold flex-1"
              />
              <Button
                onClick={() => handleDiscreteAlert('needs_attention')}
                disabled={callingAlert === 'needs_attention' || !alertPhone}
                className="bg-red-600 hover:bg-red-700 text-white font-bold border-2 border-black flex-1"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                {callingAlert === 'needs_attention' ? '📞 Calling...' : '🔊 Audio Alert'}
              </Button>
            </div>
          </div>
          <p className="text-center text-gray-700 text-sm mt-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Audio Alert will trigger a call to the restaurant with the following message:<br />
            &quot;Hello, one of our members is dining with you and has discretely alerted us that your restroom needs immediate attention&quot;
          </p>
        </div>

        {/* PAST LOO REVIEWS Title */}
        <div className="text-red-600 font-bold text-4xl text-center mb-3" 
             style={{ 
               fontFamily: 'Fredoka, sans-serif',
               letterSpacing: '0.08em',
               textTransform: 'uppercase'
             }}
             data-testid="user-name">
          Past Loo Reviews
        </div>

        {/* Search Bar - Below Title */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-4" data-testid="search-form">
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by restaurant name or city..."
            className="flex-1 bg-white border-2 border-black text-black text-base font-bold"
            data-testid="search-input"
          />
          <Button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 border-2 border-black"
            data-testid="search-btn"
          >
            Search
          </Button>
          {search && (
            <Button
              type="button"
              onClick={() => {
                setSearch('');
                fetchReviews();
              }}
              variant="outline"
              className="border-2 border-gray-800 text-gray-800 font-bold"
              data-testid="clear-search-btn"
            >
              Clear
            </Button>
          )}
        </form>

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-20" data-testid="loading-message">
            <p className="street-text text-3xl text-red-600 mb-4" 
               style={{ letterSpacing: '2px' }}>Loading reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20" data-testid="no-reviews-message">
            <p className="street-text text-3xl text-red-600 mb-4" 
               style={{ letterSpacing: '2px' }}>No reviews yet!</p>
            <p className="text-gray-900 text-lg font-bold response-text" 
               style={{ letterSpacing: '0.8px' }}>Be the first to drop a review.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="reviews-container">
            {reviews.slice(0, 4).map((review) => {
              const condition = getConditionBadge(review.general_condition);
              const supplies = getSuppliesBadge(review.supplies);
              const maintenance = getMaintenanceBadge(review.maintenance || 5);
              
              return (
                <div key={review.id} className="review-card" data-testid={`review-card-${review.id}`}>
                  {/* Restaurant Info with Overall Rating */}
                  <div className="mb-4 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between mb-2">
                        <h2 className="text-2xl text-red-600 font-bold" 
                            style={{ 
                              fontFamily: 'Inter, sans-serif',
                              letterSpacing: '0.05em',
                              textShadow: 'none',
                              fontWeight: '700'
                            }}
                            data-testid="review-restaurant-name">
                          {review.restaurant_name}
                        </h2>
                        <span className="text-gray-800 text-base font-bold response-text ml-3" 
                              style={{ letterSpacing: '0.8px' }}
                              data-testid="review-date">
                          {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      
                      <p className="text-gray-800 text-base font-bold response-text" 
                         style={{ letterSpacing: '0.8px' }}
                         data-testid="review-address">{review.address}</p>
                      <p className="text-blue-700 text-base font-bold response-text" 
                         style={{ letterSpacing: '0.8px' }}
                         data-testid="review-city">
                        {review.city}{review.state ? `, ${review.state}` : ''}
                      </p>
                    </div>

                    {/* Overall Rating */}
                    <div className="ml-4 text-center">
                      <div className="flex flex-col items-center">
                        {review.overall_rating === 1 && (
                          <img 
                            src="https://customer-assets.emergentagent.com/job_ab3cc9c0-1e7a-4d44-b103-5b0d8320f833/artifacts/o44vc304_IMG_3041.jpeg"
                            alt="Disgusting"
                            className="w-16 h-16 object-contain"
                          />
                        )}
                        {review.overall_rating === 2 && (
                          <img 
                            src="https://static.thenounproject.com/png/5000928-200.png"
                            alt="Needs Work"
                            className="w-16 h-16 object-contain"
                          />
                        )}
                        {review.overall_rating === 3 && (
                          <span className="text-5xl">👍</span>
                        )}
                        {review.overall_rating === 4 && (
                          <img 
                            src="https://customer-assets.emergentagent.com/job_ab3cc9c0-1e7a-4d44-b103-5b0d8320f833/artifacts/wear2a7c_IMG_3052.jpeg"
                            alt="Excellent"
                            className="w-16 h-16 object-contain"
                          />
                        )}
                        <span className="text-sm font-bold mt-1 text-gray-900 response-text" 
                              style={{ letterSpacing: '1px' }}>
                          {['DISGUSTING', 'NEEDS WORK!', 'ACCEPTABLE', 'EXCELLENT'][review.overall_rating - 1]}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ratings with Sliders - Bad to Good (left to right) */}
                  <div className="mb-4 space-y-4">
                    <div className="bg-white p-4 rounded border-2 border-black">
                      <p className="text-red-600 text-lg font-bold mb-2" 
                         style={{ 
                           fontFamily: 'Fredoka, sans-serif',
                           letterSpacing: '0.08em',
                           textTransform: 'uppercase',
                           fontSize: '1.15rem' 
                         }}>
                        How is the overall cleanliness?
                      </p>
                      <div className="space-y-2">
                        <Slider
                          value={[review.general_condition]}
                          min={1}
                          max={10}
                          step={1}
                          className="w-full pointer-events-none"
                          data-testid="review-condition-slider"
                        />
                        <div className="flex justify-between text-center">
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Nasty</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Clean</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Pristine</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className={`sticker-badge ${condition.class}`} data-testid="review-condition">
                            {condition.label} ({review.general_condition}/10)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded border-2 border-black">
                      <p className="text-red-600 text-lg font-bold mb-2"
                         style={{ 
                           fontFamily: 'Fredoka, sans-serif',
                           letterSpacing: '0.08em',
                           textTransform: 'uppercase',
                           fontSize: '1.15rem' 
                         }}>
                        How are the supplies?
                      </p>
                      <div className="space-y-2">
                        <Slider
                          value={[review.supplies]}
                          min={1}
                          max={10}
                          step={1}
                          className="w-full pointer-events-none"
                          data-testid="review-supplies-slider"
                        />
                        <div className="flex justify-between text-center">
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Bring Your Own!</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Acceptable</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Perfect</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className={`sticker-badge ${supplies.class}`} data-testid="review-supplies">
                            {supplies.label} ({review.supplies}/10)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded border-2 border-black">
                      <p className="text-red-600 text-lg font-bold mb-2"
                         style={{ 
                           fontFamily: 'Fredoka, sans-serif',
                           letterSpacing: '0.08em',
                           textTransform: 'uppercase',
                           fontSize: '1.15rem' 
                         }}>
                        Is everything in good condition?
                      </p>
                      <div className="space-y-2">
                        <Slider
                          value={[review.maintenance || 5]}
                          min={1}
                          max={10}
                          step={1}
                          className="w-full pointer-events-none"
                          data-testid="review-maintenance-slider"
                        />
                        <div className="flex justify-between text-center">
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Not At All</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Almost</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-red-700 text-xs font-bold"
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.05em',
                                 textTransform: 'uppercase'
                               }}>Yes</p>
                          </div>
                        </div>
                        <div className="text-center">
                          <span className={`sticker-badge ${maintenance.class}`} data-testid="review-maintenance">
                            {maintenance.label} ({review.maintenance || 5}/10)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Restroom Types */}
                  {review.restroom_types && review.restroom_types.length > 0 && (
                    <div className="mb-4">
                      <p className="text-gray-900 text-lg font-bold mb-2 response-text" 
                       style={{ letterSpacing: '1px', fontSize: '1.15rem' }}>What types of restrooms were available?</p>
                      <div className="flex flex-wrap gap-2">
                        {review.restroom_types.includes('family') && (
                          <span className="sticker-badge bg-blue-500 text-white border-blue-700">Family</span>
                        )}
                        {review.restroom_types.includes('handicap') && (
                          <span className="sticker-badge bg-purple-500 text-white border-purple-700">Handicap</span>
                        )}
                        {review.restroom_types.includes('gender_neutral') && (
                          <span className="sticker-badge bg-green-500 text-white border-green-700">Gender Neutral</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Comments */}
                  <div className="mb-4">
                    <p className="text-gray-900 text-lg font-bold mb-2 response-text" 
                       style={{ letterSpacing: '1px', fontSize: '1.15rem' }}>Comments:</p>
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-red-600">
                      <p className="text-gray-900 text-base whitespace-pre-wrap font-bold response-text" 
                         style={{ letterSpacing: '0.6px', lineHeight: '1.7' }}
                         data-testid="review-comments">
                        {review.comments}
                      </p>
                    </div>
                  </div>

                  {/* Fun URL Button */}
                  {review.fun_url && (
                    <div className="mb-4 text-center">
                      <a
                        href={review.fun_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block graffiti-heading text-xl py-3 px-6 bg-gradient-to-r from-yellow-400 via-red-500 to-blue-500 text-white border-4 border-black rounded transform hover:scale-105 transition-transform"
                        style={{
                          textShadow: '2px 2px 0px rgba(0,0,0,0.5)'
                        }}
                      >
                        For Shitz & Giggles click here! 🚽
                      </a>
                    </div>
                  )}

                  {/* Reviewer Info */}
                  <div className="text-sm text-gray-600 font-bold">
                    <span data-testid="review-reviewer">By {review.reviewer_name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white border-4 border-red-500 rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-red-600 font-bold flex items-center gap-2"
                         style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Trash2 className="w-6 h-6" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="text-gray-700">
              This action is <strong>permanent</strong> and cannot be undone. All your reviews and data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="delete-password" className="text-gray-700 font-bold">
                Enter your password to confirm:
              </Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your password"
                className="mt-2 border-2 border-gray-300"
                data-testid="delete-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="delete-confirmation" className="text-gray-700 font-bold">
                Type <span className="text-red-600">DELETE</span> to confirm:
              </Label>
              <Input
                id="delete-confirmation"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value.toUpperCase())}
                placeholder="Type DELETE"
                className="mt-2 border-2 border-gray-300"
                data-testid="delete-confirmation-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletePassword('');
                  setDeleteConfirmation('');
                }}
                variant="outline"
                className="flex-1 border-2 border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deletingAccount || deleteConfirmation !== 'DELETE'}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="confirm-delete-btn"
              >
                {deletingAccount ? 'Deleting...' : 'Delete My Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;